<template>
  <div class="preferences-container">
    <b-row no-gutters
      v-if="preferences.length > 0 && !isLoadingPreferences"
      class="w-100 flex-column card-holding-container"
    >
      <b-row class="" no-gutters>
        <b-col
          md="3"
          class="card-column"
          v-for="preference in preferences"
          :key="preference.id"
        >
          <b-card
            @click="navigateToViewPref(preference.id)"
            @mouseenter="showCardHover(preference.id)"
            @mouseleave="undoCardHover(preference.id)"
            class="preference-card"
            no-body
          >
            <div
              class="d-flex justify-content-end position-absolute card-icon-container normalize-zIndex"
            >
              <div class="d-flex">
               
                <div
                  v-if="
                    !expandHover ||
                      (showInactiveExpandHover &&
                        activeExpandHoverId !== preference.id)
                  "
                  class="empty-icon-avatar d-flex align-items-center justify-content-center"
                >
                  <img
                    class="cursor-pointer"
                    src="@/assets/icons/card_delete.svg"
                    alt
                  />
                </div>
                <div
                  class="delete-hover-avatar position-relative d-flex align-items-center justify-content-center"
                  v-if="expandHover && activeExpandHoverId === preference.id"
                  @click.stop="showDeleteModal(preference)"
                >
                  <img
                    class="cursor-pointer"
                    src="@/assets/icons/card_delete_hover.svg"
                    alt=""
                  />
                </div>
              </div>
            </div>
            <b-card-body class="d-flex flex-column justify-content-between">
              <b-row class="d-flex mx-0 w-80">
                <div
                  class="mr-4" :style="{paddingBottom: '20px'}"
                  v-for="category in preference.categories"
                  :key="category.id"
                >
                <p class="primary-dark font-family-medium">{{ category.title }}</p>
                </div>
              </b-row>
              <div class="d-flex flex-wrap description-wrapper">
                <p
                  class="primary--text font-weight-normal pre-line description "
                >
                  {{ ellipsify(preference.detail, 100) }}
                </p>
              </div>
              <div>
                <h5
                  class="secondary--text font-family-regular w-100 mb-0 pt-4 pb-0"
                >
                  {{
                    createdOrModifiedDate(
                      preference.created,
                      preference.lastEdited
                    )
                  }}
                </h5>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-row>
    <!-- Skeleton loader -->
    <b-row v-if="isLoadingPreferences" class="pl-4 pr-4">
      <b-col md="3" class="mb-3 px-2" v-for="index in 4" :key="index">
        <b-card class="p-1" style="height:22rem">
          <div
            class="d-flex justify-content-end position-absolute card-icon-container"
          >
            <div class="d-flex">
              <b-skeleton type="avatar" size="4.4rem"></b-skeleton>
            </div>
          </div>
          <div
            class="d-flex mb-4 align-items-center justify-content-between w-100"
          >
            <b-skeleton class="w-25 ml-2 mt-3"></b-skeleton>
          </div>
          <b-skeleton class="mb-3 ml-2 mt-5 w-75"></b-skeleton>
          <b-skeleton class="mb-3 ml-2 w-50"></b-skeleton>
          <b-skeleton class="mb-3 ml-2 w-50"></b-skeleton>
        </b-card>
      </b-col>
    </b-row>
    <!-- End skeleton loader -->
    <div
      class="no-assigned-msg pr-5 pt-5 pb-5 pl-2 d-flex flex-column justify-content-center align-items-center"
      v-if="!isLoadingPreferences && preferences.length === 0"
    >
      <h4 class="font-md primary--text font-family-medium">
        No preferences found.
      </h4>
    </div>
    
    <b-modal
      centered
      body-class="delete-modal"
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
      v-model="showDelete"
      v-if="showDelete"
    >
      <b-container fluid>
        <b-button
          @click="showDelete = false"
          class="d-flex position-absolute close-btn"
        >
          <img class="close-icon" src="@/assets/icons/close_sidebar.svg" alt />
        </b-button>
        <b-row
          class="pt-2 mb-5 d-flex flex-column"
          :style="{ paddingLeft: '15px' }"
        >
          <h2
            v-if="!isPADeleted"
            class="modal_header font-md"
            :style="{ marginBottom: 0 }"
          >
            Are you sure?
          </h2>
          <h2 v-if="isPADeleted" class="modal_header font-md">
            Successfully deleted
          </h2>
          <h4 v-if="!isPADeleted" class="font-md subtext">
            Do you want to delete the preference?
          </h4>
          <h4 v-else class="subtext font-md">
            Preference has been successfully deleted.
          </h4>
        </b-row>
        <b-row align-v="center">
          <b-button
            :style="{ marginLeft: 'auto' }"
            @click="removePreference"
            v-if="!isPADeleted"
            :disabled="isSavingDelete || isPADeleted"
            class="btn-danger px-5 py-3 mr-5 btn-pill reduce-whitespace"
          >
            <b-spinner
              v-if="isSavingDelete"
              style="width: 14px; height: 14px"
              variant="light"
              label="Spinning"
            ></b-spinner>
            {{ `${isSavingDelete ? "" : "Delete"}` }}
          </b-button>
        </b-row>
      </b-container>
    </b-modal>
    <!-- Owners read preferences only, so no "add preference" action. -->
    <b-avatar
      v-if="!isLoadingPreferences && !isOwnerUser"
      button
      @click="navigateToAddPref"
      size="4.8rem"
      class="floating-action-btn mb-5 position-fixed cursor-pointer"
    >
      <img src="@/assets/icons/floating_plus.svg" alt="" />
    </b-avatar>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment-timezone";
import { getCategories } from "@/apis/common/index";
import {
  deletePreference,
  deletePreferenceAttachment,
  getCustomer,
  getPreferences,
  patchPreferences,
  postPreferences,
  postPreferencesAttachment
} from "@/apis/customers/index";
import { ellipsify, isOwner } from "@/utils/utils";
import { postAttachments } from "@/apis/common/index";
import { getAssistantDetails } from "@/apis/assistants";
export default {
  name: "AddCategory",
  components: {},
  mounted() {
    this.customerID = this.$route.params.profileId;
    getCustomer(this.customerID)
      .then(res => {
        let { firstName, lastName, avatarThumbnail, email } = res.data.user;
        this.customerFirstName = firstName;
        this.customerLastName = lastName;
        this.customerFullname = `${this.customerFirstName} ${this.customerLastName}`;
        this.customerAvatar = avatarThumbnail;
        this.customerMail = email;
        this.customerUser = res.data.user;
      })
      .catch(err => {
        console.log(err);
      });
    if (this.$route.params.uuid) {
      getAssistantDetails(this.$route.params.uuid)
        .then(res => {
          let { firstName, lastName } = res.data.user;
          this.assistantFullname = `${firstName} ${lastName}`;
        })
        .catch(err => console.log(err));
    }
    this.getPref();
    this.fetchCategoryList();
  },
  methods: {
    ellipsify,
    trackPreferenceDelete() {
      this.$gtag.event("Preference - Delete", {
        event_category: "feature"
      });
    },
    humanizeTime(input) {
      return moment(input)
        .local()
        .format("DD MMM, YYYY hh:mm a");
    },
    createdOrModifiedDate(createdDate, modifiedDate) {
      createdDate = moment(String(createdDate)).format("MM/DD/YYYY hh:mm:ss");
      modifiedDate = moment(String(modifiedDate)).format("MM/DD/YYYY hh:mm:ss");

      if (moment(modifiedDate).isAfter(createdDate)) {
        return `Modified at ${this.humanizeTime(modifiedDate)}`;
      }
      return `Created at ${this.humanizeTime(createdDate)}`;
    },
    removePreference() {
      this.isSavingDelete = true;
      this.trackPreferenceDelete();
      deletePreference(
        this.$route.params.profileId,
        this.selectedPreference.id
      ).then(() => {
        this.isSavingDelete = false;
        this.showDelete = false;
        this.isLoadingPreferences = true;
        this.getPref();
      });
    },
    showDeleteModal(preference) {
      this.showDelete = true;
      this.selectedPreference = preference;
    },
    navigate(routeOne, routeTwo) {
      if (this.$route.name === "CustomerDetails") {
        this.$router.push({
          name: routeOne,
          params: {
            profileId: this.$route.params.profileId,
            customerFullname: this.customerFullname
          }
        });
      } else if (this.$route.name === "PACustomerDetails") {
        this.$router.push({
          name: routeTwo,
          params: {
            profileId: this.$route.params.profileId,
            uuid: this.$route.params.uuid,
            customerFullname: this.customerFullname,
            assistantFullname: this.assistantFullname
          }
        });
      }
    },
    navigateView(routeOne, routeTwo, id) {
      if (this.$route.name === "CustomerDetails") {
        this.$router.push({
          name: routeOne,
          params: {
            profileId: this.$route.params.profileId,
            customerFullname: this.customerFullname,
            prefID: id
          }
        });
      } else if (this.$route.name === "PACustomerDetails") {
        this.$router.push({
          name: routeTwo,
          params: {
            profileId: this.$route.params.profileId,
            uuid: this.$route.params.uuid,
            customerFullname: this.customerFullname,
            assistantFullname: this.assistantFullname,
            prefID: id
          }
        });
      }
    },
    navigateToAddPref() {
      this.navigate("AddPreference", "PAAddPreference");
    },
    navigateToViewPref(id) {
      this.navigateView("ViewEditPreference", "PAViewPreference", id);
    },
    showCardHover(id) {
      this.activeExpandHoverId = id;
      this.expandHover = true;
      this.showInactiveExpandHover = true;
    },
    undoCardHover(id) {
      this.inactiveExpandHoverId = id;
      this.expandHover = false;
    },
    deleteAttachment(index) {
      this.attachments.splice(index, 1);
    },
    viewPreference(index) {
      this.view = true;
      this.preferenceId = this.preferences[index].id;
      for (let j = 0; j < this.preferences[index].attachments.length; j++) {
        this.attachments.push(this.preferences[index].attachments[j]);
        this.attachments[j].loader = false;
      }
      this.details = this.preferences[index].detail;
      for (let i = 0; i < this.preferences[index].categories.length; i++) {
        this.selectedCategory.push(
          this.categories.find(
            item => item.id === this.preferences[index].categories[i].id
          )
        );
        this.selectedCategories.push(
          this.categories.find(
            item => item.id === this.preferences[index].categories[i].id
          ).id
        );
      }
    },
    deletePrefAttachment(index) {
      if (this.attachments[index].id) {
        this.attachmentDeleteLoader = true;
        const attachmentid = this.attachments[index].id;
        deletePreferenceAttachment(
          this.customerID,
          this.preferenceId,
          attachmentid
        )
          .then(() => {
            this.attachmentDeleteLoader = false;
            this.attachments.splice(index, 1);
          })
          .catch(() => {
            this.attachmentDeleteLoader = false;
          });
      } else {
        this.attachments.splice(index, 1);
      }
    },
    postPref() {
      const data = {
        profile: this.customerID,
        detail: this.details,
        categories: [
          this.selectedCategories[this.selectedCategories.length - 1]
        ]
      };
      this.saveLoader = true;
      if (this.selectedCategories.length > 0 && this.details.length > 0) {
        postPreferences(this.customerID, data)
          .then(res => {
            this.preferenceId = res.data.id;
            for (let j = 0; j < this.attachments.length; j++) {
              this.saveLoader = true;
              let myFile = new File(
                [this.attachments[j]],
                `${this.attachments[j].name}`
              );
              const formData = new FormData();
              formData.append("owner", this.profileUserId);
              formData.append("attachmentFile", myFile);
              postAttachments(formData)
                .then(res => {
                  const data = {
                    preference: this.preferenceId,
                    attachment: res.data.id
                  };
                  postPreferencesAttachment(
                    this.customerID,
                    this.preferenceID,
                    data
                  )
                    .then(() => {
                      if (
                        this.attachmentIds.length === this.attachments.length
                      ) {
                        this.saveLoader = false;
                        this.closeAdd();
                        this.getPref();
                      }
                    })
                    .catch(err => {
                      console.log(err);
                    });
                })
                .catch(err => {
                  console.log(err);
                  this.saveLoader = false;
                })
                .finally(() => {
                  this.saveLoader = false;
                  this.getPref();
                  this.closeAdd();
                });
            }
          })
          .catch(err => {
            console.log(err);
          })
          .finally(() => {
            this.saveLoader = false;
            this.closeAdd();
            this.getPref();
          });
      } else {
        this.saveLoader = false;
      }
    },
    patchPref() {
      this.saveLoader = true;
      const data = {
        profile: this.customerID,
        detail: this.details,
        categories: [
          this.selectedCategories[this.selectedCategories.length - 1]
        ]
      };
      if (this.selectedCategories.length > 0 && this.details.length > 0) {
        patchPreferences(this.customerID, this.preferenceId, data)
          .then(res => {
            this.saveLoader = false;
            this.preferenceId = res.data.id;
            for (let j = 0; j < this.attachments.length; j++) {
              this.saveLoader = true;
              if (this.attachments[j].name) {
                let myFile = new File(
                  [this.attachments[j]],
                  `${this.attachments[j].name}`
                );
                const formData = new FormData();
                formData.append("owner", this.profileUserId);
                formData.append("attachmentFile", myFile);
                postAttachments(formData)
                  .then(res => {
                    const data = {
                      preference: this.preferenceId,
                      attachment: res.data.id
                    };
                    postPreferencesAttachment(
                      this.customerID,
                      this.preferenceId,
                      data
                    )
                      .then(() => {
                        this.saveLoader = false;
                        this.closeView();
                      })
                      .catch(err => console.log(err));
                  })
                  .catch(err => {
                    console.log(err);
                    this.saveLoader = false;
                  })
                  .finally(() => {
                    this.saveLoader = false;
                  });
              }
            }
          })
          .catch(err => {
            console.log(err);
          })
          .finally(() => {
            this.getPref();
            this.closeView();
          });
      } else {
        this.saveLoader = false;
      }
    },
    getSelectedCategory(selected) {
      this.selectedCategories.push(selected.id);
    },
    removeSelectedOption(removedOption) {
      this.selectedCategories.pop(removedOption);
    },
    getPref() {
      getPreferences(this.$route.params.profileId)
        .then(res => {
          this.preferences = [...res.data.results].map(item => {
            item.detail = item.detail.replace(/<(.|\n)*?>/g, " ").trim();
            return item;
          });
        })
        .catch(err => {
          console.log(err.response);
          this.isLoadingPreferences = false;
        })
        .finally(() => {
          this.isLoadingPreferences = false;
        });
    },
    deletePref() {
      deletePreference(this.customerID, this.preferenceId)
        .then(() => {
          this.getPref();
          this.closeView();
        })
        .catch(err => {
          console.log(err);
        });
    },
    customerInitials(user) {
      let fullName = `${user.firstName} ${user.lastName}`;
      return fullName.match(/\b(\w)/g).join("");
    },
    fetchCategoryList() {
      getCategories()
        .then(res => {
          this.categories = [...res.data.results].map(item => {
            item.text = item.title;
            return item;
          });
        })
        .catch(err => {
          console.log(err);
        });
    },
    resetForm() {
      this.saveLoader = false;
      this.selectedCategory = [];
      this.selectedCategories.splice(0, this.selectedCategories.length);
      this.details = "";
      this.attachments.splice(0, this.attachments.length);
    },
    closeAdd() {
      this.add = false;
      this.resetForm();
    },
    closeView() {
      this.view = false;
      this.resetForm();
    },
    fileChange(event) {
      for (let i = 0; i < event.target.files.length; i++) {
        this.attachments.push(event.target.files[i]);
      }
    }
  },
  computed: {
    ...mapState("authModule", ["role", "profileUserId"]),
    isOwnerUser() {
      return isOwner();
    }
  },
  data() {
    return {
      selectedPreference: null,
      customerFullname: "",
      showDelete: false,
      isPADeleted: false,
      isSavingDelete: false,
      assistantFullname: "",
      isLoadingPreferences: true,
      expandHover: false,
      activeExpandHoverId: "",
      inactiveExpandHoverId: "",
      showInactiveExpandHover: true,
      attachmentUploading: false,
      preferences: [],
      preferenceId: "",
      attachmentIds: [],
      categories: [],
      customerAvatar: null,
      customerFirstName: "",
      attachments: [],
      saveLoader: false,
      fileURL: [],
      removeSelectedCategory: [],
      selectedCategory: [],
      view: false,
      customerLastName: "",
      categories2: [],
      selectedCategories: [],
      details: "",
      attachmentDeleteLoader: false,
      customerMail: "",
      customerUser: "",
      add: false
    };
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>;
<style scoped lang="scss">
@import "~@/scss/variables/colors.scss";

.preferences-container,
.avatar-container {
  margin-top: 5rem;
}
.card-icon-container {
  right: 1rem;
  top: 0.2rem;
}
.preferences-row {
  margin-bottom: 10rem;
}
.expand-container {
  right: 1rem;
  top: 1.8rem;
}
button.btn.close-btn {
  right: -2.5rem;
  top: -2rem;
  background: none;
}
.delete-btn {
  margin-right: 3rem;
  margin-top: 3rem;
  height: 3rem;
  widows: 3rem;
  background: $danger 0% 0% no-repeat padding-box;
  border-radius: 50%;
}
.close-icon {
}
.delete-icon {
  width: 1.5rem;
  height: 1.5rem;
  background: transparent;
}

button.btn.close-btn-delete {
  right: -2.5rem;
  top: -2rem;
  background: none;
}
.nav-search-icon-container {
  right: 1rem;
  top: 1.5rem;
}
.nav-search-icon-container1 {
  right: 1rem;
  top: 1rem;
}
.nav-search-icon-container img {
  width: 1.2rem;
  height: 1.2rem;
}
.nav-search-icon-container1 img {
  width: 1.5rem;
  height: 1.5rem;
}
.attachment-icon {
  width: 1.5rem;
  height: 1.5rem;
}
.attachment-light {
  opacity: 0.3;
}
.floating-action-btn {
  right: 3rem;
  z-index: 2;
  bottom: 0;
}
.preference-card {
  min-height: 127px;
  height: 100%;
  padding: 14px 10px;
  cursor: pointer;
  .card-body {
    padding: 0;
  }
  p {
    margin: 0;
    line-height: normal;
  }
  .description {
    line-height: 1.5;
  }
}
.card-column {
  padding: 2.5px;
  margin: 0;
}
.card-holding-container {
  padding: 2.5px;
}
::v-deep .modal-body {
  padding-bottom: 2rem;
  
  button.btn.close-btn {
    right: -3rem;
    top: -2.7rem;
    background: none;
  }
}

</style>
